import { useAuth } from "@clerk/clerk-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { BeforeAfterDemo } from "../components/BeforeAfterDemo";
import { ProcessingPanel } from "../components/ProcessingPanel";
import { UpgradeSection } from "../components/UpgradeSection";
import { trackEvent } from "../lib/analytics";
import { getPlanButtonUrl } from "../lib/flow";
import { useLicense } from "../lib/stripe/useLicense";

const OGP_IMAGE =
	"https://og.fucina.casa/api/og?title=Amazon%E3%83%BB%E6%A5%BD%E5%A4%A9%E3%83%BB%E3%83%A1%E3%83%AB%E3%82%AB%E3%83%AA%E3%80%81%E5%85%A8%E9%83%A8%E3%81%8A%E3%81%BE%E3%81%8B%E3%81%9B%E3%80%82&description=EC%E5%95%86%E5%93%81%E7%94%BB%E5%83%8F%E3%81%AE%E8%83%8C%E6%99%AF%E9%99%A4%E5%8E%BB%E3%83%BB%E3%82%B5%E3%82%A4%E3%82%BA%E8%87%AA%E5%8B%95%E5%A4%89%E6%8F%9B&type=top&locale=ja&bg=EEF2FF&accent_color=6366F1";

export default function Home() {
	const { isSignedIn } = useAuth();
	const { license, isLoading, isPro, portalUrl } = useLicense();

	useEffect(() => {
		trackEvent("lp_viewed");
	}, []);

	return (
		<>
			<Helmet>
				<title>しろぬきや — EC商品画像の背景除去・サイズ自動変換</title>
				<meta
					name="description"
					content="EC商品画像の背景除去・サイズ自動変換。Amazon・楽天・メルカリなどEC各モールやSNS規格に対応。インストール不要、ブラウザで完結。"
				/>
				<meta
					property="og:title"
					content="Amazon・楽天・メルカリ、全部おまかせ。"
				/>
				<meta
					property="og:description"
					content="EC商品画像の背景除去・サイズ自動変換。インストール不要、画像はサーバーに送りません。"
				/>
				<meta property="og:url" content="https://shironukiya.fucina.casa" />
				<meta property="og:type" content="website" />
				<meta property="og:site_name" content="しろぬきや" />
				<meta property="og:image" content={OGP_IMAGE} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta
					name="twitter:title"
					content="Amazon・楽天・メルカリ、全部おまかせ。"
				/>
				<meta
					name="twitter:description"
					content="EC商品画像の背景除去・サイズ自動変換。インストール不要、画像はサーバーに送りません。"
				/>
				<meta name="twitter:image" content={OGP_IMAGE} />
			</Helmet>
			<main className="min-h-screen overflow-x-hidden">
				{/* Header */}
				<header className="border-b border-gray-100 bg-white sticky top-0 z-10">
					<div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
						<div className="flex items-center gap-2">
							<span className="text-2xl">✂️</span>
							<span className="font-bold text-lg text-gray-900">
								しろぬきや
							</span>
							<span className="text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
								Beta
							</span>
						</div>
						{isPro ? (
							<a
								href="/mypage"
								className="inline-flex items-center text-sm bg-gradient-to-r from-purple-500 to-blue-500 text-white px-3 py-1.5 rounded-full font-medium hover:opacity-90 transition"
							>
								✨ マイページ
							</a>
						) : (
							<a
								href={getPlanButtonUrl(isSignedIn ?? false)}
								className="inline-flex items-center text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 min-h-[48px] rounded-lg hover:opacity-90 transition font-medium"
							>
								¥500/月 プラン
							</a>
						)}
					</div>
				</header>

				{/* Hero + DropZone (ファーストビュー統合) */}
				<section className="bg-gradient-to-b from-gray-50 to-white">
					<div className="max-w-5xl mx-auto px-4 pt-10 pb-2">
						{/* キャッチコピー */}
						<div className="text-center mb-8">
							<h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 leading-snug">
								画像をドロップして即・背景除去
							</h1>
							<p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mb-4">
								Amazon・楽天・Yahoo!規格やSNSサイズに自動変換。
								<strong className="text-blue-700">インストール不要</strong>
								で画像はサーバーに送られません。
							</p>
							<div className="flex justify-center gap-4 sm:gap-6 text-sm text-gray-500 flex-wrap">
								<span className="flex items-center gap-1">
									<span className="text-green-500">✓</span> インストール不要
								</span>
								<span className="flex items-center gap-1">
									<span className="text-green-500">✓</span>{" "}
									Amazon/楽天/Yahoo!対応
								</span>
								<span className="flex items-center gap-1">
									<span className="text-green-500">✓</span> SNS全サイズ対応
								</span>
								<span className="flex items-center gap-1">
									<span className="text-green-500">✓</span> 無料5枚/日
								</span>
							</div>
						</div>

						{/* before/after デモ */}
						<div className="mb-6">
							<BeforeAfterDemo />
							<p className="text-center text-sm text-gray-400 mt-3">
								← AI が背景を自動検出・除去
							</p>
						</div>

						{/* DropZone prompt */}
						<div className="flex items-center gap-3 mb-2">
							<div className="flex-1 h-px bg-gray-200" />
							<p className="text-sm font-medium text-gray-500 whitespace-nowrap">
								↓ あなたの画像をドロップして今すぐ試す
							</p>
							<div className="flex-1 h-px bg-gray-200" />
						</div>
					</div>

					{/* ProcessingPanel (DropZone + Results) */}
					<ProcessingPanel isPro={isPro} />
				</section>

				{/* プラットフォーム一覧 */}
				<section className="bg-white py-16 border-t border-gray-100">
					<div className="max-w-5xl mx-auto px-4">
						<h2 className="text-center text-2xl font-bold text-gray-900 mb-2">
							対応プラットフォーム
						</h2>
						<p className="text-center text-gray-500 mb-10">
							背景除去後、ワンクリックで各規格サイズに自動変換・ダウンロード
						</p>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
							{/* ECモール */}
							<div>
								<h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-4">
									ECモール
								</h3>
								<div className="grid grid-cols-2 gap-2.5">
									{[
										["Amazon", "1000×1000"],
										["楽天市場", "700×700"],
										["Yahoo!ショッピング", "1000×1000"],
										["au PAYマーケット", "640×640"],
										["Qoo10", "500×500"],
										["メルカリ", "1200×900"],
									].map(([name, size]) => (
										<div
											key={name}
											className="flex flex-col p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-blue-200 transition-colors"
										>
											<span className="font-medium text-sm text-gray-800 leading-snug">
												{name}
											</span>
											<span className="text-sm text-gray-400 mt-0.5">
												{size}px
											</span>
										</div>
									))}
								</div>
							</div>

							{/* SNS */}
							<div>
								<h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-4">
									SNS
								</h3>
								<div className="grid grid-cols-2 gap-2.5">
									{[
										["Instagram 正方形", "1080×1080"],
										["Instagram Stories", "1080×1920"],
										["X (Twitter)", "1200×675"],
										["TikTok", "1080×1920"],
										["YouTubeサムネ", "1280×720"],
										["透過PNG", "元サイズ"],
									].map(([name, size]) => (
										<div
											key={name}
											className="flex flex-col p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-purple-200 transition-colors"
										>
											<span className="font-medium text-sm text-gray-800 leading-snug">
												{name}
											</span>
											<span className="text-sm text-gray-400 mt-0.5">
												{size}px
											</span>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Upgrade section (Stripe統合) */}
				<UpgradeSection
					license={license}
					isLoading={isLoading}
					portalUrl={portalUrl}
				/>

				{/* Footer */}
				<footer className="text-center py-8 text-gray-400 text-sm border-t border-gray-100">
					<p>© 2026 しろぬきや</p>
					<p className="mt-1">使用モデル: ISNet (MIT License)</p>
					<div className="mt-2 flex justify-center gap-4">
						<a href="/terms" className="hover:text-gray-600 transition">
							利用規約
						</a>
						<a href="/privacy" className="hover:text-gray-600 transition">
							プライバシーポリシー
						</a>
						<a href="/tokushoho" className="hover:text-gray-600 transition">
							特定商取引法に基づく表記
						</a>
						<a href="/contact" className="hover:text-gray-600 transition">
							お問い合わせ
						</a>
					</div>
				</footer>
			</main>
		</>
	);
}
